import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server"
import Spinner from "../../Shared/Spinner"

const STATUSES = ['Open', 'Assigned', 'In Progress', 'Resolved']
const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent']

const priorityBadge = { Low: 'secondary', Medium: 'warning', High: 'danger', Urgent: 'dark' }
const statusBadge = { Open: 'primary', Assigned: 'info', 'In Progress': 'warning', Resolved: 'success' }

const ComplaintDetail = () => {
    const location = useLocation()
    const navigate = useNavigate()

    const [complaint, setComplaint] = useState(null)
    const [activities, setActivities] = useState([])
    const [users, setUsers] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Modal state
    const [modal, setModal] = useState(null) // 'assign' | 'status' | 'resolve' | 'comment'

    // Form values for modals
    const [assignData, setAssignData] = useState({ assigned_to: '', note: '' })
    const [statusData, setStatusData] = useState({ status: '', note: '' })
    const [resolveData, setResolveData] = useState({ resolution_notes: '' })
    const [commentData, setCommentData] = useState({ note: '' })

    const publicId = location.state?.complaint?.public_id

    useEffect(() => {
        if (!publicId) {
            navigate('/complaints')
            return
        }
        fetchComplaint()
        fetchUsers()
    }, [])

    const fetchComplaint = async () => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/complaints/${publicId}`)
            const data = response?.data
            setComplaint(data)
            setActivities(data?.activities || [])
            setStatusData(prev => ({ ...prev, status: data?.status || '' }))
        } catch (error) {
            if (error?.response?.status === 401) {
                localStorage.removeItem('user')
                navigate('/login')
            } else if (error?.response?.status === 403) {
                toast.error(error?.response?.data?.detail || 'Forbidden')
                navigate('/')
            } else {
                toast.error(error?.response?.data?.detail || 'Failed to load complaint')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const fetchUsers = async () => {
        try {
            const response = await Server.get('/users/')            
            setUsers(response?.data || [])
        } catch (_) {}
    }

    const closeModal = () => {
        if (isSubmitting) return
        setModal(null)
    }

    // Assign
    const handleAssign = async () => {
        if (!assignData.assigned_to) {
            toast.error('Please select a user')
            return
        }
        setIsSubmitting(true)
        try {
            const response = await Server.put(`/complaints/${publicId}/assign`, assignData)
            if (response.status === 200) {
                toast.success('Complaint assigned successfully')
                setModal(null)
                fetchComplaint()
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to assign complaint')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Change status
    const handleStatusChange = async () => {
        if (!statusData.status) {
            toast.error('Please select a status')
            return
        }
        setIsSubmitting(true)
        try {
            const response = await Server.put(`/complaints/${publicId}/status`, statusData)
            if (response.status === 200) {
                toast.success('Status updated successfully')
                setModal(null)
                fetchComplaint()
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to update status')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Resolve
    const handleResolve = async () => {
        setIsSubmitting(true)
        try {
            const response = await Server.put(`/complaints/${publicId}/resolve`, resolveData)
            if (response.status === 200) {
                toast.success('Complaint resolved')
                setModal(null)
                fetchComplaint()
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to resolve complaint')
        } finally {
            setIsSubmitting(false)
        }
    }

    // Add comment
    const handleComment = async () => {
        if (!commentData.note.trim()) {
            toast.error('Comment cannot be empty')
            return
        }
        setIsSubmitting(true)
        try {
            const response = await Server.post(`/complaints/${publicId}/comment`, commentData)
            if (response.status === 201 || response.status === 200) {
                toast.success('Comment added')
                setCommentData({ note: '' })
                setModal(null)
                fetchComplaint()
            }
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Failed to add comment')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handlePrint = () => {
        const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

        const navy    = '#1e3a5f'
        const blue    = '#2563a8'
        const light   = '#f4f7fb'
        const border  = '#ccdcee'
        const green   = '#198754'

        const statusColors  = { Open: '#0d6efd', Assigned: '#0aa2c0', 'In Progress': '#d97706', Resolved: '#198754', Closed: '#6c757d' }
        const priorityColors = { Low: '#6c757d', Medium: '#d97706', High: '#dc3545', Urgent: '#212529' }

        const statusBg   = statusColors[complaint.status]   || '#6c757d'
        const priorityBg = priorityColors[complaint.priority] || '#6c757d'

        const fmtDate = (str) => {
            if (!str) return '—'
            try {
                const d = new Date(str)
                if (isNaN(d.getTime())) return esc(str)
                return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`
            } catch { return esc(str) }
        }

        const stored      = JSON.parse(localStorage.getItem('user') || '{}')
        const branch      = stored?.branch  || {}
        const company     = branch?.company || {}
        const companyName = company.name    || branch.name || ''
        const branchName  = branch.name     || ''
        const contact     = company.resp_phone || ''
        const address     = company.address    || ''

        const createdDate  = fmtDate(complaint.created_at)
        const dueDate      = fmtDate(complaint.due_date)
        const complaintRef = complaint.id
            ? `#${String(complaint.id).padStart(5,'0')}`
            : `#${String(complaint.public_id || '').slice(-8).toUpperCase()}`
        const printTime = new Date().toLocaleString()

        const row = (label, value) =>
            `<div style="display:flex;padding:5px 0;border-bottom:1px solid ${border};">
               <span style="width:115px;flex-shrink:0;font-size:8px;color:#666;text-transform:uppercase;letter-spacing:0.3px;padding-top:1px;">${label}</span>
               <span style="font-size:9.5px;font-weight:600;color:#111;">${esc(value) || '&mdash;'}</span>
             </div>`

        const actRows = activities.length
            ? activities.map(a => `
                <tr>
                  <td style="padding:5px 8px;border-bottom:1px solid ${border};font-size:8.5px;">${esc(a.activity || a.type || '')}</td>
                  <td style="padding:5px 8px;border-bottom:1px solid ${border};font-size:8.5px;">${esc(a.note || '')}</td>
                  <td style="padding:5px 8px;border-bottom:1px solid ${border};font-size:8.5px;">${esc(a.user || '')}</td>
                  <td style="padding:5px 8px;border-bottom:1px solid ${border};font-size:8.5px;white-space:nowrap;">${esc(a.date || a.timestamp || '')}</td>
                </tr>`).join('')
            : `<tr><td colspan="4" style="padding:10px;text-align:center;font-size:8.5px;color:#aaa;">No activity recorded</td></tr>`

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Complaint ${esc(complaintRef)}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Arial,sans-serif; font-size:9px; color:#222; background:#fff; }
  .page { width:185mm; margin:0 auto; }
  @page { size:A4 portrait; margin:12mm; }
  @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } .page { width:100%; } }
</style>
</head>
<body>
<div class="page">

  <div style="height:5px;background:${blue};"></div>

  <div style="background:${navy};color:#fff;padding:12px 16px;display:flex;justify-content:space-between;align-items:flex-start;">
    <div>
      <div style="font-size:15px;font-weight:700;letter-spacing:0.4px;">${esc(companyName)}</div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:20px;font-weight:800;letter-spacing:1px;line-height:1;">${esc(complaintRef)}</div>
    </div>
  </div>

  <div style="background:${light};border-left:1px solid ${border};border-right:1px solid ${border};padding:8px 16px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
    <span style="font-size:7.5px;color:#777;text-transform:uppercase;letter-spacing:0.4px;">Status</span>
    <span style="background:${statusBg};color:#fff;font-size:8.5px;font-weight:700;padding:2px 12px;border-radius:20px;">${esc(complaint.status)}</span>
    <span style="font-size:7.5px;color:#777;text-transform:uppercase;letter-spacing:0.4px;margin-left:4px;">Priority</span>
    <span style="background:${priorityBg};color:#fff;font-size:8.5px;font-weight:700;padding:2px 12px;border-radius:20px;">${esc(complaint.priority)}</span>
    <span style="margin-left:auto;font-size:8.5px;color:#555;">Category: <strong style="color:#111;">${esc(complaint.category) || '&mdash;'}</strong></span>
    <span style="font-size:8.5px;color:#555;margin-left:12px;">Response: <strong style="color:#111;">${esc(complaint.response_type) || '&mdash;'}</strong></span>
  </div>

  <div style="display:flex;border:1px solid ${border};border-top:none;">
    <div style="width:50%;padding:12px 16px;border-right:1px solid ${border};">
      <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${blue};padding-bottom:5px;border-bottom:2px solid ${blue};margin-bottom:6px;">Complaint Information</div>
      ${row('Assigned To', complaint.assigned_to)}
      ${row('Due Date', dueDate)}
      ${row('Created At', createdDate)}
    </div>
    <div style="width:50%;padding:12px 16px;">
      <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${blue};padding-bottom:5px;border-bottom:2px solid ${blue};margin-bottom:6px;">Customer Information</div>
      ${row('Name', complaint.customer_name)}
      ${row('Phone', complaint.contact_number)}
      ${row('Alt. Phone', complaint.alternative_number)}
      ${row('Village', complaint.village_name)}
      ${row('Zone', complaint.zone_name)}
      ${complaint.location_id ? row('Account No.', complaint.location_id) : ''}
    </div>
  </div>

  <div style="border:1px solid ${border};border-top:none;padding:12px 16px;">
    <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${blue};margin-bottom:6px;">Description</div>
    <div style="background:${light};border-left:3px solid ${blue};padding:8px 12px;font-size:9.5px;color:#333;line-height:1.6;min-height:36px;">${esc(complaint.description) || '<span style="color:#bbb;">No description provided.</span>'}</div>
  </div>

  ${complaint.resolution_notes ? `
  <div style="border:1px solid ${border};border-top:none;padding:12px 16px;">
    <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${green};margin-bottom:6px;">Resolution Notes</div>
    <div style="background:#f0faf4;border-left:3px solid ${green};padding:8px 12px;font-size:9.5px;color:#333;line-height:1.6;">${esc(complaint.resolution_notes)}</div>
  </div>` : ''}

  <div style="border:1px solid ${border};border-top:none;padding:12px 16px;">
    <div style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;color:${blue};margin-bottom:8px;">Activity Log</div>
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr style="background:${navy};color:#fff;">
          <th style="padding:5px 8px;font-size:8px;font-weight:600;text-align:left;width:22%;">Action</th>
          <th style="padding:5px 8px;font-size:8px;font-weight:600;text-align:left;">Note</th>
          <th style="padding:5px 8px;font-size:8px;font-weight:600;text-align:left;width:22%;">By</th>
          <th style="padding:5px 8px;font-size:8px;font-weight:600;text-align:left;width:22%;">Date</th>
        </tr>
      </thead>
      <tbody>${actRows}</tbody>
    </table>
  </div>

  <div style="display:flex;border:1px solid ${border};border-top:none;">
    <div style="width:50%;padding:12px 16px;border-right:1px solid ${border};">
      <div style="font-size:8px;color:#777;margin-bottom:28px;">Customer Signature</div>
      <div style="border-top:1px solid #aaa;padding-top:4px;font-size:8px;color:#aaa;">Signature &amp; Date</div>
    </div>
    <div style="width:50%;padding:12px 16px;">
      <div style="font-size:8px;color:#777;margin-bottom:28px;">Received / Handled By</div>
      <div style="border-top:1px solid #aaa;padding-top:4px;font-size:8px;color:#aaa;">Name, Signature &amp; Date</div>
    </div>
  </div>

  <div style="background:${light};border:1px solid ${border};border-top:none;padding:5px 16px;display:flex;justify-content:space-between;">
    <span style="font-size:7px;color:#aaa;">${esc(companyName)} &mdash; Internal Document</span>
    <span style="font-size:7px;color:#aaa;">Printed: ${esc(printTime)}</span>
  </div>

  <div style="height:4px;background:${blue};"></div>

</div>
</body>
</html>`

        const win = window.open('', '_blank', 'width=900,height=950')
        if (!win) {
            toast.error('Popup blocked — please allow popups to print.')
            return
        }
        win.document.write(html)
        win.document.close()
        win.focus()
        setTimeout(() => win.print(), 400)
    }

    if (isLoading) return <Spinner/>
    if (!complaint) return null

    const priorityColor = priorityBadge[complaint.priority] || 'secondary'
    const statusColor = statusBadge[complaint.status] || 'secondary'
    const isResolved = complaint.status === 'Resolved' || complaint.status === 'Closed'

    return (
        <>
            <div className="row mb-2">
                <div className="col-md-12">
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/complaints')}>
                        ← Back to Complaints
                    </button>
                </div>
            </div>

            {/* Header card */}
            <div className="card mb-3">
                <div className="card-header p-2 bg-info text-white d-flex justify-content-between align-items-center">
                    <h3 className="card-title mb-0">Complaint Details</h3>
                    <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-light" onClick={() => setModal('assign')} disabled={isResolved}>Assign</button>
                        <button className="btn btn-sm btn-light" onClick={() => setModal('status')}>Change Status</button>
                        <button className="btn btn-sm btn-success" onClick={() => setModal('resolve')} disabled={isResolved}>Resolve</button>
                        <button className="btn btn-sm btn-light" onClick={() => setModal('comment')}>Add Comment</button>
                        <button className="btn btn-sm btn-outline-light" onClick={handlePrint}>🖨️ Print</button>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-7">
                            {complaint.title && <h4 className="mb-2">{complaint.title}</h4>}
                            <small className="text-muted text-uppercase fw-semibold">Description</small>
                            <p className="text-muted mt-1">{complaint.description || '—'}</p>
                            {complaint.resolution_notes && (
                                <>
                                    <hr className="my-2"/>
                                    <small className="text-success text-uppercase fw-semibold">Resolution Notes</small>
                                    <p className="mt-1 mb-0">{complaint.resolution_notes}</p>
                                </>
                            )}
                        </div>
                        <div className="col-md-5">
                            <table className="table table-sm table-borderless">
                                <tbody>
                                    <tr>
                                        <td className="text-muted fw-semibold">Status</td>
                                        <td><span className={`badge bg-${statusColor}`}>{complaint.status}</span></td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Priority</td>
                                        <td><span className={`badge bg-${priorityColor}`}>{complaint.priority}</span></td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Category</td>
                                        <td>{complaint.category || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Response Type</td>
                                        <td>{complaint.response_type || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Assigned To</td>
                                        <td>{complaint.assigned_to || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Due Date</td>
                                        <td>{complaint.due_date || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Created at</td>
                                        <td>{complaint.created_at || '—'}</td>
                                    </tr>
                                    <tr>
                                        <td className="text-muted fw-semibold">Created by</td>
                                        <td>{complaint.created_by || '—'}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <hr className="my-3"/>

                    {/* Customer / Location info */}
                    {complaint.location_id ? (
                        <div className="row g-3">
                            <div className="col-md-3">
                                <small className="text-muted d-block">Customer</small>
                                <span>{complaint.customer_name || '—'}</span>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted d-block">Contact</small>
                                <span>{complaint.contact_number || '—'}</span>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted d-block">Village</small>
                                <span>{complaint.village_name || '—'}</span>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted d-block">Zone</small>
                                <span>{complaint.zone_name || '—'}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="row g-3">
                            <div className="col-md-3">
                                <small className="text-muted d-block">Full Name</small>
                                <span>{complaint.customer_name || '—'}</span>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted d-block">Contact</small>
                                <span>{complaint.contact_number || '—'}</span>
                            </div>
                            <div className="col-md-2">
                                <small className="text-muted d-block">Alt. Number</small>
                                <span>{complaint.alternative_number || '—'}</span>
                            </div>
                            <div className="col-md-2">
                                <small className="text-muted d-block">Village</small>
                                <span>{complaint.village_name || '—'}</span>
                            </div>
                            <div className="col-md-2">
                                <small className="text-muted d-block">Zone</small>
                                <span>{complaint.zone_name || '—'}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Activities timeline */}
            <div className="card">
                <div className="card-header p-2 bg-light">
                    <h5 className="mb-0">Activity Log</h5>
                </div>
                <div className="card-body p-0">
                    {activities.length === 0 ? (
                        <p className="text-muted p-3 mb-0">No activity yet.</p>
                    ) : (
                        <table className="table table-sm table-hover mb-0">
                            <thead className="table-dark">
                                <tr>
                                    <th>Action</th>
                                    <th>Note</th>
                                    <th>By</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activities.map((act, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <span className="badge bg-secondary">{act.activity || act.type || '—'}</span>
                                        </td>
                                        <td>{act.note || '—'}</td>
                                        <td>{act.user || '—'}</td>
                                        <td className="text-nowrap text-muted">{act.date || act.timestamp || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Assign Modal */}
            <Modal show={modal === 'assign'} onHide={closeModal} backdrop={isSubmitting ? 'static' : true}>
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>Assign Complaint</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group mb-3">
                        <label className="form-label">Assign To <span className="text-danger">*</span></label>
                        <select className="form-select" value={assignData.assigned_to} onChange={e => setAssignData(prev => ({ ...prev, assigned_to: e.target.value }))}>
                            <option value="">— Select User —</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.name || u.username}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Note (optional)</label>
                        <textarea className="form-control" rows={3} value={assignData.note} onChange={e => setAssignData(prev => ({ ...prev, note: e.target.value }))} placeholder="Optional note…"/>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>Cancel</Button>
                    <Button variant="info" onClick={handleAssign} disabled={isSubmitting}>
                        {isSubmitting ? 'Assigning…' : 'Assign'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Change Status Modal */}
            <Modal show={modal === 'status'} onHide={closeModal} backdrop={isSubmitting ? 'static' : true}>
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>Change Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group mb-3">
                        <label className="form-label">Status <span className="text-danger">*</span></label>
                        <select className="form-select" value={statusData.status} onChange={e => setStatusData(prev => ({ ...prev, status: e.target.value }))}>
                            <option value="">— Select Status —</option>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Note (optional)</label>
                        <textarea className="form-control" rows={3} value={statusData.note} onChange={e => setStatusData(prev => ({ ...prev, note: e.target.value }))} placeholder="Optional note…"/>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>Cancel</Button>
                    <Button variant="info" onClick={handleStatusChange} disabled={isSubmitting}>
                        {isSubmitting ? 'Updating…' : 'Update Status'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Resolve Modal */}
            <Modal show={modal === 'resolve'} onHide={closeModal} backdrop={isSubmitting ? 'static' : true}>
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>Resolve Complaint</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label className="form-label">Resolution Notes (optional)</label>
                        <textarea className="form-control" rows={4} value={resolveData.resolution_notes} onChange={e => setResolveData({ resolution_notes: e.target.value })} placeholder="Describe how this was resolved…"/>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>Cancel</Button>
                    <Button variant="success" onClick={handleResolve} disabled={isSubmitting}>
                        {isSubmitting ? 'Resolving…' : 'Mark Resolved'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add Comment Modal */}
            <Modal show={modal === 'comment'} onHide={closeModal} backdrop={isSubmitting ? 'static' : true}>
                <Modal.Header closeButton={!isSubmitting}>
                    <Modal.Title>Add Comment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label className="form-label">Comment <span className="text-danger">*</span></label>
                        <textarea className="form-control" rows={4} value={commentData.note} onChange={e => setCommentData({ note: e.target.value })} placeholder="Write your comment…"/>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal} disabled={isSubmitting}>Cancel</Button>
                    <Button variant="info" onClick={handleComment} disabled={isSubmitting}>
                        {isSubmitting ? 'Saving…' : 'Add Comment'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <br/>
        </>
    )
}

export default ComplaintDetail
